import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { api } from '../utils/api';
import { Search, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useWebSocketContext } from '../context/WebSocketContext';

export const Entries = () => {
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const { subscribe } = useWebSocketContext();

  const fetchData = React.useCallback(async () => {
    console.log('🔄 [Entries] fetchData() INICIADO');
    try {
      console.log('📡 [Entries] Haciendo peticiones API...');
      
      const [movementsRes, productsRes] = await Promise.all([
        api.getMovements(),
        api.getProducts()
      ]);
      
      console.log('✅ [Entries] Respuestas API recibidas');
      console.log('   - Movements total:', movementsRes.data.length);
      console.log('   - Products total:', productsRes.data.length);
      
      const productsMap = {};
      productsRes.data.forEach(p => {
        productsMap[p.id] = p;
      });
      
      setProducts(productsMap);
      const entries = movementsRes.data.filter(m => m.type === 'entry');
      
      console.log('   - Entries filtradas:', entries.length);
      
      setMovements(entries);
      
      console.log('✅ [Entries] Estado React ACTUALIZADO');
    } catch (error) {
      console.error('❌ [Entries] Error en fetchData:', error);
      toast.error('Error al cargar entradas');
    }
    console.log('🏁 [Entries] fetchData() COMPLETADO');
  }, []);

  // Carga inicial
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // POLLING DESACTIVADO - causaba recargas visuales y pérdida de foco
  // useEffect(() => {
  //   const pollingInterval = setInterval(() => {
  //     fetchData();
  //   }, 5000);
  //   return () => clearInterval(pollingInterval);
  // }, [fetchData]);

  // Subscribe to WebSocket updates (instantáneo)
  useEffect(() => {
    console.log('🔌 [Entries] Suscribiéndose a WebSocket...');
    
    const unsubscribe = subscribe((message) => {
      console.log('========================================');
      console.log('📥 [Entries] HANDLER WebSocket ejecutado');
      console.log('📨 Mensaje recibido:', message);
      console.log('🔍 Tipo:', message.type);
      console.log('🔍 Movement type:', message.movement?.type);
      
      if (message.type === 'stock_update' && message.movement?.type === 'entry') {
        console.log('✅ [Entries] Condiciones cumplidas: stock_update + entry');
        console.log('🔄 [Entries] Llamando a fetchData()...');
        
        fetchData(); // Ahora funcionará correctamente
        
        console.log('📢 [Entries] Mostrando toast de nueva entrada');
        toast.success('Nueva entrada registrada', { duration: 2000 });
      } else {
        console.log(`⚠️ [Entries] Condiciones NO cumplidas`);
        console.log(`   - Esperaba: type='stock_update' y movement.type='entry'`);
        console.log(`   - Recibió: type='${message.type}' y movement.type='${message.movement?.type}'`);
      }
      console.log('========================================');
    });

    console.log('✅ [Entries] Suscripción WebSocket completada');
    
    return () => {
      console.log('🔌 [Entries] Desuscribiéndose de WebSocket...');
      unsubscribe();
    };
  }, [subscribe, fetchData]);

  const filteredMovements = movements.filter(m => {
    const product = products[m.product_id];
    if (!product) return false;
    const searchLower = searchTerm.toLowerCase();
    return product.name.toLowerCase().includes(searchLower) || 
           product.barcode.includes(searchTerm);
  });

  return (
    <div className="space-y-6">
      <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">Entradas</h1>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Historial de Entradas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar por producto o código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm rounded-2xl pl-10 border-2"
              />
            </div>
          </div>

          <ResponsiveTable
            data={filteredMovements.map(movement => ({
              ...movement,
              product: products[movement.product_id]
            }))}
            columns={[
              {
                key: 'created_at',
                label: 'Fecha',
                className: 'uppercase tracking-wider text-xs',
                render: (item) => (
                  <span className="font-mono text-sm">
                    {new Date(item.created_at).toLocaleString('es-ES', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                )
              },
              {
                key: 'product',
                label: 'Producto',
                className: 'uppercase tracking-wider text-xs',
                render: (item) => (
                  <div>
                    <p className="font-medium">{item.product?.name || 'Producto no encontrado'}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-mono">{item.product?.barcode}</p>
                  </div>
                )
              },
              {
                key: 'quantity',
                label: 'Cantidad',
                className: 'uppercase tracking-wider text-xs',
                render: (item) => (
                  <span className="font-mono font-bold text-green-600 dark:text-green-400">
                    +{item.quantity}
                  </span>
                )
              },
              {
                key: 'cost_per_unit',
                label: 'Coste/Unidad',
                className: 'uppercase tracking-wider text-xs',
                render: (item) => (
                  <span className="font-mono">
                    {item.cost_per_unit ? `€${item.cost_per_unit.toFixed(2)}` : '-'}
                  </span>
                )
              },
              {
                key: 'notes',
                label: 'Notas',
                className: 'uppercase tracking-wider text-xs',
                render: (item) => (
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {item.notes || '-'}
                  </span>
                )
              }
            ]}
            emptyMessage="No se encontraron entradas"
          />
        </CardContent>
      </Card>
    </div>
  );
};